import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Field,
  Label,
  Option,
  useId,
  Tooltip,
} from '@fluentui/react-components';
import { maxTagCount } from '../../constants/maximums';
import { errorMessages } from '../../errors';
import { useMeasurementTypeSelectorStyles } from '../../styles';
import { getGoalsOptionText } from './uet-tag-selector-utils';
import { RequiredDropdown } from '../validated/required-dropdown';

export const UetTagSelector = ({
  fetchedUetTags,
  isDisabled,
  onSelect,
  orderedFetchedUetTags,
  selected,
  showError,
  areActiveConversionGoalsMissing,
}) => {
  const dropdownId = useId('uetTagSelector');

  const i18n = useContext(I18nContext);
  const placeholder = i18n.getString(_TL_('Select UET tag'));
  const delimiter = i18n.getString(_TL_(', '));
  const buttonText = useMemo(() => {
    if (selected.length === 0) {
      return placeholder;
    }
    return selected.map(tagId => fetchedUetTags[tagId].name).join(delimiter);
  }, [delimiter, fetchedUetTags, placeholder, selected]);

  const hoverMessage = areActiveConversionGoalsMissing ? i18n.getString(_TL_('The selected conversion lift does not have any active conversion goals')) : null;
  const goalsUnit = i18n.getString(_TL_('active goal'));
  const goalsUnitPlural = i18n.getString(_TL_('active goals'));
  const noGoals = i18n.getString(_TL_('no active goals'));

  const onOptionSelect = useCallback((e, data) => {
    onSelect(data.selectedOptions);
  }, [onSelect]);

  const styles = useMeasurementTypeSelectorStyles();

  const [fieldProps, setFieldProps] = useState(null);

  const onValidateUetTags = useCallback(({ isValid, messages }) => {
    if (isValid) {
      setFieldProps(null);
    } else {
      setFieldProps({
        validationMessage: i18n.getString(errorMessages[messages[0]]),
        validationState: 'error',
      });
    }
  }, [i18n]);

  return (
    <div>
      <Label
        disabled={isDisabled}
        id={dropdownId}
        required
      >
        {i18n.getString(_TL_('UET tags'))}
      </Label>
      <Tooltip relationship="description" content={hoverMessage} withArrow visible={areActiveConversionGoalsMissing}>
        <Field {...(showError ? fieldProps : null)}>
          <RequiredDropdown
            aria-labelledby={dropdownId}
            button={<span className={styles.truncatedText}>{buttonText}</span>}
            className={styles.dropdown}
            disabled={isDisabled || areActiveConversionGoalsMissing}
            errorMessage="UET_REQUIRED"
            multiselect
            onOptionSelect={onOptionSelect}
            validationProps={{
              data: { selectedOptions: selected },
              field: 'selectedOptions',
              onValidate: onValidateUetTags,
            }}
          >
            {orderedFetchedUetTags.map((goal) => {
              const { id } = goal;
              const isMaxedOut = selected.length === maxTagCount;
              const isOptionDisabled = isMaxedOut && !selected.includes(id);

              return (
                <Option
                  disabled={isOptionDisabled}
                  key={id}
                  value={id}
                  className={`uet-tag-selector-option-${id}`}
                >
                  {getGoalsOptionText({
                    goal,
                    none: noGoals,
                    plural: goalsUnitPlural,
                    singular: goalsUnit,
                  })}
                </Option>
              );
            })}
          </RequiredDropdown>
        </Field>
      </Tooltip>
    </div>
  );
};

UetTagSelector.propTypes = {
  fetchedUetTags: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    goalsCount: PropTypes.number.isRequired,
  })).isRequired,
  isDisabled: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  orderedFetchedUetTags: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    goalsCount: PropTypes.number.isRequired,
  })).isRequired,
  selected: PropTypes.arrayOf(PropTypes.number).isRequired,
  showError: PropTypes.bool,
  areActiveConversionGoalsMissing: PropTypes.bool,
};

UetTagSelector.defaultProps = {
  showError: false,
  areActiveConversionGoalsMissing: false,
};
